import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Camera, Upload, Trash2, ShieldCheck,
  MapPin, Clock, Database, Fingerprint,
  Zap, ChevronRight, Image as ImageIcon,
  Microscope, Lock, AlertTriangle, Loader2,
  X, CheckCircle2,
} from 'lucide-react';

import AssetPericialService from '../../services/AssetPericialService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, LIMITES } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: VistoriaFotos
 * ------------------------------------------------------------
 * Álbum geral de evidências fotográficas (fora dos 6 fatores).
 *
 *   ✔ Hash SHA-256 real (crypto.subtle)
 *   ✔ GPS real via navigator.geolocation
 *   ✔ Marca d'água aplicada pelo AssetPericialService
 *   ✔ Base64 persistente (não URL.createObjectURL)
 *   ✔ Navegação tipada via ETAPA
 * VERSÃO: 2.0.0
 * ============================================================
 */

/* fatorId "virtual" para o álbum geral — pasta ANEXOS_GERAIS */
const FATOR_ANEXOS = 'anexos_gerais';

export default function VistoriaFotos({
  laudo = {},
  setLaudo,
  setNavegacao,
  peritoAtivo = 'MONTE_HERMOM_USER',
  infoLaudo = { numero: 'MH-2026-0042', engenheiro: 'MARCUS FERRAZ' },
  fotosData,
  setFotosData,
}) {
  const [fotos, setFotos] = useState(
    fotosData || laudo?.fotosGerais || []
  );
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  /* ---------- sincronização com estado global ---------- */
  useEffect(() => {
    if (fotosData && fotosData !== fotos) {
      setFotos(fotosData);
    } else if (laudo?.fotosGerais && laudo.fotosGerais !== fotos) {
      setFotos(laudo.fotosGerais);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laudo?.fotosGerais, fotosData]);

  /* ---------- persiste alteração nos dois canais ---------- */
  const sincronizarEstado = useCallback((lista) => {
    setFotos(lista);
    if (typeof setLaudo === 'function') {
      setLaudo({ ...laudo, fotosGerais: lista });
    }
    if (typeof setFotosData === 'function') {
      setFotosData(lista);
    }
  }, [laudo, setLaudo, setFotosData]);

  /* ---------- upload / captura ---------- */
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setErro(null);
    setMensagem(null);
    setProcessando(true);
    setProgresso({ atual: 0, total: files.length });

    const processadas = [];

    for (const file of files) {
      try {
        // Validação de tipo
        if (!file.type.startsWith('image/')) {
          throw new Error(`"${file.name}" não é uma imagem`);
        }

        // Validação de tamanho
        const mb = file.size / 1024 / 1024;
        if (mb > LIMITES.ARQUIVO_MAX_MB) {
          throw new Error(`"${file.name}" excede ${LIMITES.ARQUIVO_MAX_MB}MB`);
        }

        const resultado = await AssetPericialService.processarCaptura(
          file,
          FATOR_ANEXOS,
          infoLaudo.numero,
          {
            perito: peritoAtivo,
            comodoLocal: null,
            tentarGPS: true,
          }
        );

        processadas.push({
          id: resultado.hashIntegridade,
          url: resultado.url,                    // base64 persistente
          file: resultado.url,
          fileName: resultado.fileName,
          folder: resultado.folder,
          hashIntegridade: resultado.hashIntegridade,
          hashOriginal: resultado.hashOriginal,
          timestamp: resultado.timestamp,
          coordenadas: resultado.metadados?.coordenadas || null,
          laudoVinculado: infoLaudo.numero,
          perito: peritoAtivo,
          legenda: '',
          metadados: resultado.metadados,
        });
      } catch (err) {
        setErro(prev => prev
          ? `${prev}\n• ${err.message}`
          : `Falha ao processar: ${err.message}`);
      } finally {
        setProgresso(prev => ({ ...prev, atual: prev.atual + 1 }));
      }
    }

    if (processadas.length > 0) {
      const listaAtualizada = [...fotos, ...processadas];
      sincronizarEstado(listaAtualizada);
      setMensagem(`${processadas.length} evidência(s) selada(s) com sucesso.`);
      setTimeout(() => setMensagem(null), 4000);
    }

    setProcessando(false);
    setProgresso({ atual: 0, total: 0 });
  };

  const onChangeInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = null;
  };

  /* ---------- remoção ---------- */
  const removerFoto = (id) => {
    const filtradas = fotos.filter(f => f.id !== id);
    sincronizarEstado(filtradas);
    setMensagem('Evidência removida.');
    setTimeout(() => setMensagem(null), 3000);
  };

  /* ---------- atualização de legenda ---------- */
  const atualizarLegenda = (id, valor) => {
    const atualizadas = fotos.map(f =>
      f.id === id ? { ...f, legenda: valor } : f
    );
    setFotos(atualizadas);
    if (typeof setLaudo === 'function') {
      setLaudo({ ...laudo, fotosGerais: atualizadas });
    }
  };

  /* ---------- persistência explícita ---------- */
  const persistirEAvancar = async () => {
    setIsSaving(true);
    setErro(null);

    try {
      await laudoApi.salvarModulo({
        report_id: infoLaudo.numero,
        perito: peritoAtivo,
        modulo: 'FOTOS_GERAIS',
        timestamp: new Date().toISOString(),
        fotos: fotos.map(f => ({
          id: f.id,
          fileName: f.fileName,
          hashIntegridade: f.hashIntegridade,
          hashOriginal: f.hashOriginal,
          coordenadas: f.coordenadas,
          legenda: f.legenda,
          metadados: f.metadados,
        })),
        totalEvidencias: fotos.length,
      });

      if (typeof setNavegacao === 'function') {
        setNavegacao(ETAPA.RESUMO);
      }
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — fotos salvas localmente e serão sincronizadas.');
        setTimeout(() => {
          if (typeof setNavegacao === 'function') setNavegacao(ETAPA.RESUMO);
        }, 1500);
      } else {
        setErro(`Falha ao salvar: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="relative font-black italic uppercase select-none">

      {/* WATERMARK DE FUNDO */}
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[150px] z-0 text-[#2F353B] font-black italic leading-none">
        MONTE HERMOM
      </div>

      <div className="max-w-6xl mx-auto relative z-10 space-y-12">

        {/* HEADER */}
        <header className="flex justify-between items-end border-b-[12px] border-[#2F353B] pb-12">
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-blue-600">
              <Camera size={32} />
              <span className="text-[12px] tracking-[0.6em] text-slate-400">
                PGLP VISUAL ASSET MANAGER
              </span>
            </div>
            <h1 className="text-[42px] text-[#2F353B] leading-none tracking-[0.1em]">
              ÁLBUM DE <span className="text-blue-600">EVIDÊNCIAS</span>
            </h1>
            <p className="text-[11px] tracking-[0.5em] text-slate-400 mt-2">
              NBR 14653 • PROTOCOLO FOTOGRÁFICO DE ALTA FIDELIDADE
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="bg-[#2F353B] px-12 py-6 rounded-[2.5rem] border-b-8 border-blue-600 text-white shadow-2xl flex items-center gap-6">
              <Database size={24} className="text-blue-400" />
              <span className="text-[18px]">{infoLaudo.numero}</span>
            </div>
          </div>
        </header>

        {/* ALERTAS */}
        {erro && (
          <div className="bg-red-900/90 text-white p-6 rounded-[2rem] flex items-start gap-4 whitespace-pre-line">
            <AlertTriangle size={22} className="shrink-0 mt-0.5" />
            <span className="text-[11px] tracking-widest leading-relaxed">{erro}</span>
          </div>
        )}

        {mensagem && (
          <div className="bg-emerald-900/90 text-white p-6 rounded-[2rem] flex items-center gap-4">
            <CheckCircle2 size={22} />
            <span className="text-[11px] tracking-widest">{mensagem}</span>
          </div>
        )}

        {/* ÁREA DE UPLOAD / CAPTURA */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* BOTÃO GALERIA */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={processando}
            className="group relative h-64 bg-white rounded-[4rem] border-4 border-dashed border-slate-200 flex flex-col items-center justify-center gap-6 transition-all hover:border-blue-600 hover:bg-blue-50/30 disabled:opacity-50"
          >
            <div className="w-20 h-20 bg-[#2F353B] rounded-full flex items-center justify-center text-white shadow-2xl group-hover:scale-110 transition-transform">
              {processando ? <Loader2 size={32} className="animate-spin" /> : <Upload size={32} />}
            </div>
            <div className="text-center">
              <span className="block text-[18px] text-[#2F353B]">
                {processando ? 'PROCESSANDO...' : 'IMPORTAR ATIVOS'}
              </span>
              <span className="text-[10px] text-slate-400 tracking-widest">
                MÁXIMO {LIMITES.ARQUIVO_MAX_MB}MB POR ARQUIVO
              </span>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={onChangeInput}
              accept="image/*"
            />
          </button>

          {/* CÂMERA DIRETA */}
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={processando}
            className="group h-64 bg-[#2F353B] rounded-[4rem] p-12 flex flex-col justify-between border-b-[12px] border-blue-600 shadow-2xl text-left disabled:opacity-50"
          >
            <div className="flex justify-between items-start w-full">
              <div className="space-y-2">
                <span className="text-blue-400 text-[10px] tracking-[0.4em]">
                  CAPTURA DIRETA
                </span>
                <h3 className="text-white text-[22px] leading-tight">
                  ATIVAR<br />CÂMERA
                </h3>
              </div>
              <Zap size={48} className="text-blue-500 opacity-50" />
            </div>
            <p className="text-[10px] text-slate-400 leading-relaxed tracking-widest">
              TODA FOTO RECEBE AUTOMATICAMENTE: GPS REAL, TIMESTAMP,
              ID DO PERITO E HASH SHA-256 DE SEGURANÇA.
            </p>
            <input
              type="file"
              ref={cameraInputRef}
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={onChangeInput}
            />
          </button>
        </section>

        {/* BARRA DE PROGRESSO DE UPLOAD */}
        {processando && progresso.total > 0 && (
          <div className="bg-white border-4 border-slate-100 rounded-[2rem] p-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] text-slate-500 tracking-widest">
                PROCESSANDO EVIDÊNCIAS
              </span>
              <span className="text-[10px] text-blue-600 tracking-widest">
                {progresso.atual} / {progresso.total}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${(progresso.atual / progresso.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* GRID DE FOTOS */}
        {fotos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-12">
            {fotos.map((foto, index) => (
              <div
                key={foto.id}
                className="group bg-white rounded-[4.5rem] border-4 border-slate-100 shadow-xl overflow-hidden transition-all hover:shadow-2xl hover:border-blue-100"
              >
                {/* IMAGEM + OVERLAY */}
                <div className="relative aspect-video bg-slate-200 overflow-hidden">
                  <img
                    src={foto.url}
                    alt={foto.legenda || `Evidência ${index + 1}`}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                  />

                  {/* RODAPÉ COM DADOS TÉCNICOS */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-8 flex flex-col justify-end gap-2">
                    <div className="flex justify-between items-end">
                      <div className="space-y-1">
                        {foto.coordenadas && (
                          <div className="flex items-center gap-2 text-white/90 text-[10px] font-black italic">
                            <MapPin size={12} className="text-blue-500" />
                            GPS: {foto.coordenadas.lat}, {foto.coordenadas.lng}
                            {foto.coordenadas.precisao && ` (±${foto.coordenadas.precisao}m)`}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-white/90 text-[10px] font-black italic">
                          <Clock size={12} className="text-blue-500" />
                          DATA: {foto.timestamp}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="block text-white/50 text-[8px] tracking-widest uppercase italic">
                          Report No.
                        </span>
                        <span className="text-blue-400 text-[12px] font-black">
                          {foto.laudoVinculado}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* BOTÃO REMOVER */}
                  <button
                    type="button"
                    onClick={() => removerFoto(foto.id)}
                    className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-all border border-white/20"
                    title="Remover"
                  >
                    <Trash2 size={20} />
                  </button>

                  {/* BADGE SEGURANÇA */}
                  <div className="absolute top-6 left-6 bg-blue-600 text-white px-6 py-2 rounded-full text-[9px] font-black italic tracking-widest flex items-center gap-3 shadow-2xl">
                    <Lock size={12} /> SECURE ASSET
                  </div>
                </div>

                {/* RODAPÉ DO CARD */}
                <div className="p-10 space-y-6">
                  <div className="flex justify-between items-center border-b border-slate-50 pb-6">
                    <div className="flex items-center gap-4 min-w-0">
                      <Fingerprint size={20} className="text-blue-600 shrink-0" />
                      <span className="text-slate-400 text-[9px] tracking-[0.4em] truncate">
                        ID: {foto.hashIntegridade?.substring(0, 16)}...
                      </span>
                    </div>
                    <span className="text-[12px] text-[#2F353B] font-black shrink-0">
                      FOTO #{String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <textarea
                    placeholder="DESCRIÇÃO TÉCNICA DO ATIVO..."
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-[2rem] p-6 text-[14px] text-[#2F353B] placeholder:text-slate-300 outline-none focus:border-blue-600 transition-all italic resize-none h-24"
                    value={foto.legenda || ''}
                    onChange={(e) => atualizarLegenda(foto.id, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/[0.02] border-4 border-dashed border-slate-200 rounded-[3rem] p-20 text-center">
            <ImageIcon size={64} className="mx-auto mb-6 text-slate-200" />
            <p className="text-slate-400 text-[12px] tracking-widest">
              NENHUMA EVIDÊNCIA REGISTRADA AINDA
            </p>
          </div>
        )}

        {/* FOOTER */}
        <footer className="pt-20 border-t-8 border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-[#2F353B] rounded-2xl flex items-center justify-center shadow-2xl">
              <Microscope className="text-blue-500" size={32} />
            </div>
            <div>
              <span className="block text-[18px] text-[#2F353B] tracking-widest">
                CURADORIA VISUAL
              </span>
              <span className="text-blue-600 text-[10px] tracking-[0.5em] font-black italic">
                {infoLaudo.engenheiro} • ENGENHARIA 2026
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={persistirEAvancar}
            disabled={isSaving}
            className="group flex items-center gap-12 bg-[#2F353B] text-white px-20 py-10 rounded-full shadow-2xl transition-all hover:bg-blue-600 active:scale-95 border-b-[8px] border-blue-900 active:border-b-0 disabled:opacity-50"
          >
            <span className="text-[18px] tracking-[0.2em]">
              {isSaving ? 'PROCESSANDO...' : 'REVISAR LAUDO'}
            </span>
            <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-all">
              {isSaving
                ? <Loader2 size={24} className="animate-spin" />
                : <ChevronRight size={24} />}
            </div>
          </button>
        </footer>
      </div>

      {/* MARCADOR DE STATUS FLUTUANTE */}
      <div className="fixed bottom-12 right-12 z-[100] bg-[#2F353B] text-white px-10 py-8 rounded-[3rem] shadow-2xl flex items-center gap-8 border-l-[15px] border-blue-600 border border-white/10 italic">
        <Zap size={30} className="text-blue-400 animate-pulse" />
        <div className="space-y-1">
          <span className="text-[14px] tracking-widest font-black block uppercase">
            Sincronização Ativa
          </span>
          <span className="text-[10px] text-slate-400 tracking-[0.4em] uppercase">
            {fotos.length} EVIDÊNCIAS NO BUFFER
          </span>
        </div>
      </div>
    </div>
  );
}